import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useTheme } from 'styled-components';
import DocImageGenerator from '@composites/DocImageGenerator';
import HeroSection from '@composites/HeroSection';
import { MetaTags } from '@components/MetaTags';
import Button from '@components/Button';
import { getEventDetails } from '@store/slices/eventSlice';
import { getImageSrc, safeJSONParse } from '@utils/fn';
import { useInbound } from '@utils/Inbound/useInbound';

import RegistrationForm from '@composites/InboundButton/components/RegistrationForm';
import DownloadPartnerProspectus from '@composites/InboundButton/components/DownloadPartnerProspectus';
import {
  InputFieldsType,
  designTypeFields,
  orderedFields,
  defaultInboundValues,
} from '@composites/InboundButton/utils';

import {
  GridContainer,
  ContentWrapper,
  SectionGrid,
  GridSpan,
} from '@pages/EventGuideUpdatePage/styles';

import { usePublicCoLocatedEventsByEventId } from '@utils/apiHooks';
import { useDesktopWidth, useMobileWidth } from '@utils/hooks';
import PartnersGroupsSection from '@pages/Partners/components/PartnersGroupsSection';
import TextButton from '@composites/TextButton';

const HIDE_IDS = [
  'linkedin',
  'twitter',
  'facebook',
  'xing',
  'instagram',
  'website',
];

const BPPackagesRegPage = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  const { registerInbound, inboundValue } = useInbound();

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [pendingAutoDownload, setPendingAutoDownload] = useState(false);
  const downloadBtnWrapRef = useRef<HTMLDivElement>(null);

  const { data: coLocatedEvents = [] } = usePublicCoLocatedEventsByEventId({
    eventId: sluggedEvent?.id,
    enabled: !!sluggedEvent?.id && !!sluggedEvent?.colocated,
  });

  const hasInboundForEvent = useMemo(() => {
    try {
      if (!inboundValue || !sluggedEvent?.id) return false;
      const obj = JSON.parse(inboundValue);
      const ev = obj?.event ?? obj?.event_id ?? obj?.eventId;
      return String(ev) === String(sluggedEvent.id);
    } catch {
      return false;
    }
  }, [inboundValue, sluggedEvent?.id]);

  useEffect(() => {
    setIsAuthorized(hasInboundForEvent);
  }, [hasInboundForEvent]);

  const inboundValues = useMemo(
    () => ({ booleanValues: defaultInboundValues, nonBooleanValues: {} }),
    []
  );

  const inputElements: InputFieldsType = useMemo(() => {
    return orderedFields
      .map(id => {
        const cfg = (inboundValues.booleanValues as Record<string, any>)[id];
        if (!cfg?.visible) return undefined;
        if (HIDE_IDS.includes(id)) return undefined;
        const base = designTypeFields.find(f => f.id === id);
        return base ? { ...base, ...cfg } : undefined;
      })
      .filter(Boolean) as InputFieldsType;
  }, [inboundValues]);

  const defaultValues = useMemo(() => {
    const inboundObj = safeJSONParse(inboundValue ?? '{}') as Record<
      string,
      string
    >;
    return inputElements.reduce<Record<string, string>>((acc, field) => {
      acc[field.id] = inboundObj[field.id] ?? '';
      return acc;
    }, {});
  }, [inboundValue, inputElements]);

  const {
    control,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<Record<string, string>>({
    mode: 'onSubmit',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [checkedApproval, setCheckedApproval] = useState(false);

  useEffect(() => {
    if (pendingAutoDownload && isAuthorized) {
      const id = setTimeout(() => {
        const btn = downloadBtnWrapRef.current?.querySelector('button');
        if (btn) {
          btn.click();
          setPendingAutoDownload(false);
        }
      }, 250);
      return () => {
        clearTimeout(id);
      };
    }
  }, [pendingAutoDownload, isAuthorized]);

  const handleChange = (fieldName: string) => (_v: string) => {
    clearErrors(fieldName);
  };

  const onSubmit = async (data: Record<string, string>) => {
    if (!checkedApproval) return;
    await registerInbound(
      {
        ...data,
        event: sluggedEvent?.id,
        path: window.location.href,
        action: 'download_bp_packages',
      },
      () => {
        setPendingAutoDownload(true);
      }
    );
  };

  const otherEvents = (coLocatedEvents || [])
    .filter(e => e?.name && e?.name !== sluggedEvent?.name)
    .map(e => e.name);

  const bodyText = otherEvents.length
    ? t('bp.body.colocated', {
        event: sluggedEvent?.name,
        others: otherEvents.join(' & '),
      })
    : t('bp.body.base', { event: sluggedEvent?.name });

  const docLabel = 'BUSINESS PARTNER PACKAGES';
  const isDesktop = useDesktopWidth();
  const isMobile = useMobileWidth();

  return (
    <>
      <MetaTags
        title={`${sluggedEvent?.name} - ${t('bp.meta-title')}`}
        description={t('bp.meta-description')}
        keywords={`${sluggedEvent?.slug}, bp packages, partner brochure`}
        image={getImageSrc(sluggedEvent?.design?.eventLogoPosSvg)}
        url={window.location.href}
        twitterCardType="summary"
        type="website"
      />

      <SectionGrid>
        <GridSpan span={12}>
          <HeroSection
            backgroundImage={getImageSrc(
              'https://media.we-conect.com/images/website/bp_packages_hero_section.jpg',
              sluggedEvent?.portfolio?.slug
            )}
            darkOverlay
            title={t('bp.hero-title')}
            // description={bodyText}
            description={t('bp.hero-description', {
              eventName: sluggedEvent?.name || '',
            })}
          />
        </GridSpan>
        <GridSpan span={12}>
          <TextButton
            title={t('bp.hero-title')?.toUpperCase()}
            subtitle={bodyText}
            hideButton
            padding="6rem 2rem 0 2rem"
          />
        </GridSpan>
        <GridSpan span={12}>
          <GridContainer oneColumn>
            <ContentWrapper>
              {!isAuthorized ? (
                <>
                  <GridContainer>
                    <ContentWrapper
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        minWidth: 0,
                        // desktop only
                        ...(!isDesktop && {
                          padding: '5rem 2%',
                          paddingBottom: '2rem',
                        }),
                      }}
                    >
                      <DocImageGenerator
                        templateSrc="https://media.we-conect.com/images/website/bp_packages_broschure.png"
                        docLabel={docLabel}
                      />
                    </ContentWrapper>
                    <form
                      onSubmit={e => {
                        e.preventDefault();
                        void handleSubmit(onSubmit)(e);
                      }}
                    >
                      <RegistrationForm
                        control={control}
                        errors={errors}
                        inputElements={inputElements}
                        setCheckedApproval={setCheckedApproval}
                        checkedApproval={checkedApproval}
                        handleChange={handleChange}
                      />

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: isMobile ? 'center' : 'flex-start',
                          marginTop: '1.25rem',
                        }}
                      >
                        <Button
                          title={t('bp.button')}
                          backgroundColor={sluggedEvent?.design?.themeColor}
                          hoverBackgroundColor={theme.color.mobilityGrey}
                          textColor={theme.color.white}
                          borderRadius={theme.borderRadius.radius10}
                          padding="0.9rem 2.4rem"
                          width="max-content"
                          disabled={!checkedApproval}
                          onClick={() => {
                            void handleSubmit(onSubmit)();
                          }}
                        />
                      </div>
                    </form>

                    {pendingAutoDownload && (
                      <div
                        ref={downloadBtnWrapRef}
                        style={{ display: 'none' }}
                        aria-hidden="true"
                      >
                        <DownloadPartnerProspectus
                          label={t('bp.button')}
                          buttonType="download_bp_packages"
                          inboundValues={{
                            booleanValues: defaultInboundValues,
                            nonBooleanValues: {},
                          }}
                        />
                      </div>
                    )}
                  </GridContainer>
                </>
              ) : (
                <div
                  ref={downloadBtnWrapRef}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '3rem',
                  }}
                >
                  <DownloadPartnerProspectus
                    label={t('bp.button')}
                    buttonType="download_bp_packages"
                    inboundValues={{
                      booleanValues: defaultInboundValues,
                      nonBooleanValues: {},
                    }}
                  />
                </div>
              )}
            </ContentWrapper>
          </GridContainer>
        </GridSpan>
        <GridSpan span={12}>
          <PartnersGroupsSection />
        </GridSpan>
      </SectionGrid>
    </>
  );
};

export default BPPackagesRegPage;
